import { orchestrator } from 'satcheljs';
import { trace } from 'owa-trace';
import { createCalendarProviderAccount } from '../utils/createCalendarProviderAccount';
import { getOAuthProviderType } from '../utils/calendarProviderTypeConverter';
import {
    reauthCalendarProviderAccount,
    reauthCalendarProviderAccountComplete,
    errorWhenReauthCalendarProviderAccount,
    calendarProviderAccountAdded,
    calendarProviderAccountRemoved,
} from '../actions/publicActions';
import { getCalendarProviderAccountForType } from '../selectors/calendarProviderAccountsSelectors';
import { removeCalendarProviderAccount as removeCalendarProviderAccountService } from '../services/removeCalendarProviderAccount';
import {
    lazyLinkAccountAndGetAccessToken,
    lazySetAddConnectedAccountHandler,
    ConnectedAccountRequest,
} from 'owa-link-account';
import {
    addCalendarProviderAccountHandler,
    ConnectedAccountResponseWithObj,
} from '../utils/addCalendarProviderAccountHandler';
import type { CalendarProviderAccountInfo } from '../types/CalendarProviderAccountInfo';
import { initializeCalendarProviderAccountAddHandlers } from './addCalendarProviderAccountOrchestrator';

orchestrator(reauthCalendarProviderAccount, async actionMessage => {
    const { providerType } = actionMessage;
    const providerAccount = getCalendarProviderAccountForType(providerType);

    if (!providerAccount) {
        // If there is no existing account then its an error reauthing
        // an account that does not exist.
        errorWhenReauthCalendarProviderAccount(providerType);
        return;
    }

    try {
        // Start the process of removing the account
        const removePromise = removeCalendarProviderAccountService(providerAccount.id);

        // Add a custom handler
        const defaultAddHandler = addCalendarProviderAccountHandler(providerType);
        const customAddHandler = async (request: ConnectedAccountRequest) => {
            // Before creating the new account we wait for the old one to be
            // removed as we can only have one account of the same type.
            await removePromise;
            calendarProviderAccountRemoved(providerType, providerAccount.id);

            // disabling tslint rule as we need to await here
            // for the finally block to work properly
            // https://github.com/palantir/tslint/issues/3933
            // tslint:disable-next-line:no-return-await
            return await defaultAddHandler(request);
        };

        const oAuthProviderType = getOAuthProviderType(providerType);
        lazySetAddConnectedAccountHandler.importAndExecute(oAuthProviderType, customAddHandler);

        // Add the account
        const response = (await lazyLinkAccountAndGetAccessToken.importAndExecute(
            oAuthProviderType
        )) as ConnectedAccountResponseWithObj<CalendarProviderAccountInfo>;
        const calendarProviderAccount = createCalendarProviderAccount(
            response.connectedAccountObject
        );

        calendarProviderAccountAdded(calendarProviderAccount);
        reauthCalendarProviderAccountComplete(calendarProviderAccount);
    } catch (error) {
        trace.warn(`Error reauthing removing calendar provider account: ${error.toString()}`);
        errorWhenReauthCalendarProviderAccount(providerType);
    } finally {
        // Re initialize the add handlers as we added a custom one earlier
        initializeCalendarProviderAccountAddHandlers();
    }
});
